import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { TeamMemberState, TeamMemberPoints, TeamMember, TeamMemberSyncResponse } from './types'
import {
  fetchTeamMembers,
  fetchTeamMemberPoints,
  bulkSyncTeamMembers,
  fetchTeamMemberInfo,
} from './actions'

const initialState: TeamMemberState = {
  loadingTeamMembers: false,
  loadingTeamMemberPoints: false,
  loadingTeamMemberInfo: false,

  teamMemberInfo: null,

  teamMembers: [],
  teamMemberPoints: [],
  filters: {
    supervisor: [],
    operationalManager: [],
    practice: [],
    functionalArea: [],
    jobCode: [],
    accessRole: [],
  },
  searchQuery: '',

  syncMessage: undefined,
  syncCounts: undefined,
  lastUpdateInfo: undefined,

  errorTeamMembers: '',
  errorTeamMemberPoints: '',
  errorTeamMemberInfo: '',
}

export const teamMemberSlice = createSlice({
  name: 'teamMember',
  initialState,
  reducers: {
    setSupervisorFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.supervisor = action.payload
    },
    setOperationalManagerFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.operationalManager = action.payload
    },
    setPracticeFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.practice = action.payload
    },
    setFunctionalAreaFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.functionalArea = action.payload
    },
    setJobCodeFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.jobCode = action.payload
    },
    setAccessRoleFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.accessRole = action.payload
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },

    clearFilters: (state) => {
      state.filters = {
        supervisor: [],
        operationalManager: [],
        practice: [],
        functionalArea: [],
        jobCode: [],
        accessRole: [],
      }
    },

    clearSearchQuery: (state) => {
      state.searchQuery = ''
    },

    clearTeamMemberInfo: (state) => {
      state.teamMemberInfo = null
    },

    clearLastUpdateInfo: (state) => {
      state.lastUpdateInfo = undefined
    },

    // Bulk update access roles in one Redux mutation to avoid N re-renders
    bulkUpdateAccessRoles: (
      state,
      action: PayloadAction<{ teamMemberId: string; accessRole: string }[]>
    ) => {
      action.payload.forEach(({ teamMemberId, accessRole }) => {
        const idx = state.teamMembers.findIndex(m => m._id === teamMemberId)
        if (idx !== -1) {
          state.teamMembers[idx].accessRole = accessRole
        }
      })
    },
  },
  extraReducers: (builder) => {
    // Fetch All Team Members
    builder
      .addCase(fetchTeamMembers.pending, (state) => {
        state.loadingTeamMembers = true
        state.errorTeamMembers = ''
      })
      .addCase(fetchTeamMembers.fulfilled, (state, action: PayloadAction<TeamMember[]>) => {
        state.loadingTeamMembers = false
        state.teamMembers = action.payload
        
        // Extract lastUpdate info from the first team member (since it's the same for all records)
        if (action.payload.length > 0 && action.payload[0].updatedAt && action.payload[0].lastCsvUploadFilename) {
          state.lastUpdateInfo = {
            date: action.payload[0].updatedAt,
            filename: action.payload[0].lastCsvUploadFilename
          }
        }
        
        state.errorTeamMembers = ''
      })
      .addCase(fetchTeamMembers.rejected, (state, action) => {
        state.loadingTeamMembers = false
        state.errorTeamMembers = action.error.message || 'Failed to fetch team members'
      })

    // Fetch Team Member Points
    builder
      .addCase(fetchTeamMemberInfo.pending, (state) => {
        state.loadingTeamMemberInfo = true
        state.errorTeamMemberInfo = ''
      })
      .addCase(fetchTeamMemberInfo.fulfilled, (state, action: PayloadAction<TeamMember>) => {
        state.loadingTeamMemberInfo = false
        state.teamMemberInfo = action.payload
        state.errorTeamMemberInfo = ''
      })
      .addCase(fetchTeamMemberInfo.rejected, (state, action) => {
        state.loadingTeamMemberInfo = false
        state.errorTeamMemberInfo = action.error.message || 'Failed to fetch team member info'
      })

    // Fetch Team Member points
    builder
      .addCase(fetchTeamMemberPoints.pending, (state) => {
        state.loadingTeamMemberPoints = true
        state.errorTeamMemberPoints = ''
      })
      .addCase(fetchTeamMemberPoints.fulfilled, (state, action: PayloadAction<TeamMemberPoints[]>) => {
        state.loadingTeamMemberPoints = false
        state.teamMemberPoints = action.payload
        state.errorTeamMemberPoints = ''
      })
      .addCase(fetchTeamMemberPoints.rejected, (state, action) => {
        state.loadingTeamMemberPoints = false
        state.errorTeamMemberPoints = action.error.message || 'Failed to fetch team member points'
      })

    // Bulk Sync Team Members
    builder
      .addCase(bulkSyncTeamMembers.pending, (state) => {
        state.loadingTeamMembers = true
        state.errorTeamMembers = ''
      })
      .addCase(bulkSyncTeamMembers.fulfilled, (state, action: PayloadAction<TeamMemberSyncResponse>) => {
        state.loadingTeamMembers = false
        state.teamMembers = action.payload.teamMembers || []
        state.syncMessage = action.payload.message
        state.syncCounts = {
          updatedCount: action.payload.updatedCount,
          addedCount: action.payload.addedCount,
          terminatedCount: action.payload.terminatedCount,
        }
        const lastUpdateInfo = {
          date: new Date().toISOString(),
          filename: action.payload.filename || 'TeamMemberSync.csv'
        }
        state.lastUpdateInfo = lastUpdateInfo
        state.filters = {
          supervisor: [],
          operationalManager: [],
          practice: [],
          functionalArea: [],
          jobCode: [],
          accessRole: [],
        }
        state.errorTeamMembers = ''
      })
      .addCase(bulkSyncTeamMembers.rejected, (state, action) => {
        state.loadingTeamMembers = false
        state.errorTeamMembers = action.error.message || 'Failed to bulk sync team members'
      })

  },
})

export const {
  setSupervisorFilter,
  setOperationalManagerFilter,
  setPracticeFilter,
  setFunctionalAreaFilter,
  setJobCodeFilter,
  setAccessRoleFilter,
  clearFilters,
  clearSearchQuery,
  setSearchQuery,
  clearTeamMemberInfo,
  clearLastUpdateInfo,
  bulkUpdateAccessRoles,
} = teamMemberSlice.actions

export default teamMemberSlice.reducer
